import type { PackageType } from './software-update-package.service';
import type { RolloutPackage, RolloutType, RolloutTarget } from './software-update-rollout.service';

export const rolloutTypeLabel: Record<RolloutType, string> = {
  app: 'App',
  os: 'OS',
  fw: 'Firmware',
};

/** Title-case a canonical machine type or subtype, e.g. `rower` -> `Rower`. */
export function titleCaseToken(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

/** Uppercase a canonical ATS id, e.g. `ats2` -> `ATS2`. */
export function formatAtsId(value: string): string {
  return value ? value.toUpperCase() : value;
}

/**
 * True when two rollout targets select the same devices (all three fields equal).
 * Used to find the active rollout a deploy would replace.
 */
export function sameRolloutTarget(
  a: RolloutTarget | null | undefined,
  b: RolloutTarget | null | undefined,
): boolean {
  return (
    (a?.machine_type ?? null) === (b?.machine_type ?? null) &&
    (a?.machine_sub_type ?? null) === (b?.machine_sub_type ?? null) &&
    (a?.ats_id ?? null) === (b?.ats_id ?? null)
  );
}

interface RolloutTargetFieldDef {
  get: (target: RolloutTarget) => string | null;
  /** Compact key for dense inline contexts (table, deploy summary). */
  shortKey: string;
  /** Full label for split-row / definition-list contexts (detail, dialogs). */
  label: string;
  format: (value: string) => string;
}

// Ordered descriptors for the three target dimensions. Value formatting matches
// the server display name: title-cased machine type / subtype, upper-cased ATS.
export const rolloutTargetFieldDefs: RolloutTargetFieldDef[] = [
  { get: (t) => t.machine_type, shortKey: 'Type', label: 'Machine type', format: titleCaseToken },
  { get: (t) => t.machine_sub_type, shortKey: 'Subtype', label: 'Subtype', format: titleCaseToken },
  { get: (t) => t.ats_id, shortKey: 'ATS', label: 'ATS', format: formatAtsId },
];

export interface RolloutTargetRow {
  label: string;
  value: string;
}

/**
 * One label-value row per SET target field, with full labels
 * (Machine type / Subtype / ATS) and formatted values; unset fields omitted.
 * For definition-list screens (detail overview, deploy/archive dialogs).
 */
export function rolloutTargetRows(target: RolloutTarget): RolloutTargetRow[] {
  const rows: RolloutTargetRow[] = [];
  for (const f of rolloutTargetFieldDefs) {
    const raw = f.get(target);
    if (raw) rows.push({ label: f.label, value: f.format(raw) });
  }
  return rows;
}

const comparableAppVersionParts: Record<string, number> = {
  'com.android.chrome': 4,
  'com.avironactive.rower': 3,
  'com.aviron.service': 3,
  'com.oem.qisda': 3,
  'com.google.android.gms': 3,
};

function parseNumericVersion(version: string, expectedParts: number): number[] | null {
  const parts = version.trim().split('.');
  if (parts.length !== expectedParts) return null;
  const nums = parts.map((part) => (/^\d+$/.test(part) ? Number.parseInt(part, 10) : NaN));
  return nums.every(Number.isFinite) ? nums : null;
}

function comparableVersion(packageName: string, version: string): number[] | null {
  const expectedParts = comparableAppVersionParts[packageName];
  if (!expectedParts) return null;
  if (packageName === 'com.google.android.gms') {
    const leading = version.match(/^\s*(\d+\.\d+\.\d+)/)?.[1];
    return leading ? parseNumericVersion(leading, expectedParts) : null;
  }
  return parseNumericVersion(version, expectedParts);
}

export function compareAppPackageVersion(
  packageName: string,
  leftVersion: string,
  rightVersion: string,
): number | null {
  const left = comparableVersion(packageName, leftVersion);
  const right = comparableVersion(packageName, rightVersion);
  if (!left || !right || left.length !== right.length) return null;
  for (let i = 0; i < left.length; i++) {
    if (left[i] > right[i]) return 1;
    if (left[i] < right[i]) return -1;
  }
  return 0;
}

export type DiffKind = 'changed' | 'same';

export interface DiffFieldEntry {
  kind: DiffKind;
  displayName: string;
  packageName: string;
  type: PackageType;
  field: string;
  fromValue: string | null;
  toValue: string | null;
}

export type PackageChangeKind = 'add' | 'remove' | 'changed' | 'same';

export interface DiffEntry {
  kind: PackageChangeKind;
  displayName: string;
  packageName: string;
  type: PackageType;
  fromVersion: string | null;
  toVersion: string | null;
}

const diffKey = (p: RolloutPackage) => `${p.type}::${p.package_name}`;

export function diffPackages(
  source: RolloutPackage[] | null,
  target: RolloutPackage[] | null,
): DiffEntry[] {
  const sourceMap = new Map<string, RolloutPackage>();
  (source ?? []).forEach((p) => sourceMap.set(diffKey(p), p));
  const targetMap = new Map<string, RolloutPackage>();
  (target ?? []).forEach((p) => targetMap.set(diffKey(p), p));

  const allKeys = new Set<string>([...sourceMap.keys(), ...targetMap.keys()]);
  const entries: DiffEntry[] = [];
  for (const key of allKeys) {
    const src = sourceMap.get(key);
    const tgt = targetMap.get(key);
    if (src && tgt) {
      entries.push({
        kind: src.version === tgt.version ? 'same' : 'changed',
        displayName: tgt.display_name || src.display_name,
        packageName: tgt.package_name,
        type: tgt.type,
        fromVersion: src.version,
        toVersion: tgt.version,
      });
    } else if (tgt) {
      entries.push({
        kind: 'add',
        displayName: tgt.display_name,
        packageName: tgt.package_name,
        type: tgt.type,
        fromVersion: null,
        toVersion: tgt.version,
      });
    } else if (src) {
      entries.push({
        kind: 'remove',
        displayName: src.display_name,
        packageName: src.package_name,
        type: src.type,
        fromVersion: src.version,
        toVersion: null,
      });
    }
  }

  const kindOrder: Record<PackageChangeKind, number> = { changed: 0, add: 1, remove: 2, same: 3 };
  const typeOrder: Record<PackageType, number> = { apk: 0, os: 1, fw: 2 };
  entries.sort((a, b) => {
    if (kindOrder[a.kind] !== kindOrder[b.kind]) return kindOrder[a.kind] - kindOrder[b.kind];
    if (typeOrder[a.type] !== typeOrder[b.type]) return typeOrder[a.type] - typeOrder[b.type];
    return a.packageName.localeCompare(b.packageName);
  });
  return entries;
}

function optionalBoolValue(value: unknown): string | null {
  if (typeof value !== 'boolean') return null;
  return value ? 'Yes' : 'No';
}

function packageAllowDowngrade(p: RolloutPackage | undefined): string | null {
  if (!p) return null;
  const raw = p as RolloutPackage & {
    allow_downgrade?: boolean | null;
    allowDowngrade?: boolean | null;
  };
  return optionalBoolValue(raw.allow_downgrade ?? raw.allowDowngrade);
}

function packageFields(p: RolloutPackage | undefined): Array<{ field: string; value: string | null }> {
  if (!p) {
    return [
      { field: 'Version', value: null },
      { field: 'Allow downgrade', value: null },
    ];
  }

  const fields = [{ field: 'Version', value: p.version || null }];
  if (p.type === 'apk') {
    const allowDowngrade = packageAllowDowngrade(p);
    if (allowDowngrade !== null) {
      fields.push({ field: 'Allow downgrade', value: allowDowngrade });
    }
  }
  return fields;
}

function fieldEntry(
  displayName: string,
  packageName: string,
  type: PackageType,
  field: string,
  fromValue: string | null,
  toValue: string | null,
): DiffFieldEntry {
  return {
    kind: fromValue === toValue ? 'same' : 'changed',
    displayName,
    packageName,
    type,
    field,
    fromValue,
    toValue,
  };
}

function diffSinglePackageRolloutFields(
  source: RolloutPackage[] | null,
  target: RolloutPackage[] | null,
  type: Extract<PackageType, 'os' | 'fw'>,
): DiffFieldEntry[] {
  const sourceTarget = (source ?? []).find((p) => p.sort_order === 1) ?? source?.[0];
  const targetTarget = (target ?? []).find((p) => p.sort_order === 1) ?? target?.[0];
  const ref = targetTarget ?? sourceTarget;
  const displayName = ref?.display_name || (type === 'os' ? 'OS' : 'Firmware');
  const packageName = ref?.package_name || displayName;
  const entries = [
    fieldEntry(
      displayName,
      packageName,
      type,
      'Version',
      sourceTarget?.version ?? null,
      targetTarget?.version ?? null,
    ),
  ];

  if (type === 'os') {
    const sourceRecovery = (source ?? []).find((p) => p.sort_order === 2);
    const targetRecovery = (target ?? []).find((p) => p.sort_order === 2);
    entries.push(
      fieldEntry(
        displayName,
        packageName,
        type,
        'Recovery version',
        sourceRecovery?.version ?? null,
        targetRecovery?.version ?? null,
      ),
    );
  }

  const kindOrder: Record<DiffKind, number> = { changed: 0, same: 1 };
  const fieldOrder: Record<string, number> = { Version: 0, 'Recovery version': 1 };
  return entries.sort((a, b) => {
    if (kindOrder[a.kind] !== kindOrder[b.kind]) return kindOrder[a.kind] - kindOrder[b.kind];
    return (fieldOrder[a.field] ?? 99) - (fieldOrder[b.field] ?? 99);
  });
}

export function diffPackageFields(
  source: RolloutPackage[] | null,
  target: RolloutPackage[] | null,
): DiffFieldEntry[] {
  const rolloutType = target?.[0]?.type ?? source?.[0]?.type;
  if (rolloutType === 'os' || rolloutType === 'fw') {
    return diffSinglePackageRolloutFields(source, target, rolloutType);
  }

  const sourceMap = new Map<string, RolloutPackage>();
  (source ?? []).forEach((p) => sourceMap.set(diffKey(p), p));
  const targetMap = new Map<string, RolloutPackage>();
  (target ?? []).forEach((p) => targetMap.set(diffKey(p), p));

  const allKeys = new Set<string>([...sourceMap.keys(), ...targetMap.keys()]);
  const entries: DiffFieldEntry[] = [];
  for (const key of allKeys) {
    const src = sourceMap.get(key);
    const tgt = targetMap.get(key);
    const ref = tgt ?? src;
    if (!ref) continue;

    const srcFields = new Map(packageFields(src).map((f) => [f.field, f.value]));
    const tgtFields = new Map(packageFields(tgt).map((f) => [f.field, f.value]));
    const fieldNames = Array.from(new Set([...srcFields.keys(), ...tgtFields.keys()]));

    for (const field of fieldNames) {
      const fromValue = srcFields.get(field) ?? null;
      const toValue = tgtFields.get(field) ?? null;
      entries.push({
        kind: fromValue === toValue ? 'same' : 'changed',
        displayName: ref.display_name,
        packageName: ref.package_name,
        type: ref.type,
        field,
        fromValue,
        toValue,
      });
    }
  }

  const kindOrder: Record<DiffKind, number> = { changed: 0, same: 1 };
  const fieldOrder: Record<string, number> = { Version: 0, 'Allow downgrade': 1 };
  const typeOrder: Record<PackageType, number> = { apk: 0, os: 1, fw: 2 };
  entries.sort((a, b) => {
    if (typeOrder[a.type] !== typeOrder[b.type]) return typeOrder[a.type] - typeOrder[b.type];
    const packageCompare = (a.displayName || a.packageName).localeCompare(b.displayName || b.packageName);
    if (packageCompare !== 0) return packageCompare;
    if (kindOrder[a.kind] !== kindOrder[b.kind]) return kindOrder[a.kind] - kindOrder[b.kind];
    return (fieldOrder[a.field] ?? 99) - (fieldOrder[b.field] ?? 99);
  });
  return entries;
}

export function groupDiffFieldEntries(entries: DiffFieldEntry[]): DiffFieldEntry[][] {
  const groups: DiffFieldEntry[][] = [];
  for (const entry of entries) {
    const key = `${entry.type}:${entry.packageName}:${entry.displayName}`;
    const last = groups[groups.length - 1];
    const lastEntry = last?.[0];
    const lastKey = lastEntry ? `${lastEntry.type}:${lastEntry.packageName}:${lastEntry.displayName}` : null;
    if (last && key === lastKey) {
      last.push(entry);
    } else {
      groups.push([entry]);
    }
  }
  return groups;
}
